import { useCallback, useRef, useState } from 'react';
import type { TeamBillingInfo } from '@overlayqa/shared';
import { useBillingRefresh } from './useBillingRefresh';
import { isSubscriptionAcknowledged } from '../lib/pollBillingUntilActive';
import { capture } from '../lib/analytics';
import { log } from '@/lib/logger';

export type ActivationSource = 'stripe_return' | 'clerk_drawer';
export type SuccessDismissMethod = 'escape' | 'backdrop' | 'auto';

export type SubscriptionActivation =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'active'; info: TeamBillingInfo; successOpen: boolean };

/**
 * Between paying and using the plan (approved prototype:
 * docs/prototypes/billing-seats, 2026-10-18). Both checkouts enter here:
 * Stripe's return and Clerk's drawer.
 *
 *   pending  -> the page hides its purchase controls behind "Updating your plan"
 *   active   -> the success message opens; after it closes the page keeps
 *               `active` so the invite step stays primary for this visit
 *   idle     -> the poll ran out or failed: the page returns as it was
 */
export function useSubscriptionActivation() {
  const refreshBilling = useBillingRefresh();
  const [activation, setActivation] = useState<SubscriptionActivation>({ status: 'idle' });
  const pollingRef = useRef(false);

  const start = useCallback(async (source: ActivationSource) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setActivation({ status: 'pending' });
    let info: TeamBillingInfo | null = null;
    let failed = false;
    try {
      info = await refreshBilling();
    } catch (error) {
      failed = true;
      log.warn('[useSubscriptionActivation] billing refresh failed', { source, error: error instanceof Error ? error.message : String(error) });
    } finally {
      pollingRef.current = false;
    }
    if (info && isSubscriptionAcknowledged(info)) {
      capture('billing_subscription_activated', {
        source,
        plan: info.plan,
        max_seats: Number.isFinite(info.maxSeats) ? info.maxSeats : null,
      });
      setActivation({ status: 'active', info, successOpen: true });
      return;
    }
    capture('billing_subscription_activation_unconfirmed', { source, reason: failed ? 'error' : 'timeout' });
    setActivation({ status: 'idle' });
  }, [refreshBilling]);

  const dismissSuccess = useCallback((method: SuccessDismissMethod) => {
    capture('billing_subscription_success_dismissed', { method });
    setActivation(current => (current.status === 'active' ? { ...current, successOpen: false } : current));
  }, []);

  return { activation, start, dismissSuccess };
}
